import { z, ZodIssueCode, ZodParsedType } from 'zod'
import { t, te } from '@/I18n'
import type {
  ZodCustomIssue,
  ZodInvalidArgumentsIssue,
  ZodInvalidDateIssue,
  ZodInvalidEnumValueIssue,
  ZodInvalidIntersectionTypesIssue,
  ZodInvalidLiteralIssue,
  ZodInvalidReturnTypeIssue,
  ZodInvalidStringIssue,
  ZodInvalidTypeIssue,
  ZodInvalidUnionDiscriminatorIssue,
  ZodInvalidUnionIssue,
  ZodIssueOptionalMessage,
  ZodNotFiniteIssue,
  ZodNotMultipleOfIssue,
  ZodTooBigIssue,
  ZodTooSmallIssue,
  ZodUnrecognizedKeysIssue,
} from 'zod/lib/ZodError'

// This code is forked from zod-i18n-map
// https://github.com/aiji42/zod-i18n
// Original code is based on i18next and has been refactored/cleaned/converted to fit with vue-i18n

const joinValues = <T extends unknown[]>(array: T, separator = ' | '): string =>
  array.map((val) => (typeof val === 'string' ? `'${val}'` : val)).join(separator)

const isRecord = (value: unknown): value is Record<string, unknown> => !(typeof value !== 'object' || value === null)

const getKeyAndValues = (
  param: unknown,
  defaultKey: string
): {
  values: Record<string, unknown>
  key: string
} => {
  if (typeof param === 'string') {
    return { key: param, values: {} }
  }

  if (isRecord(param)) {
    const key = 'key' in param && typeof param.key === 'string' ? param.key : defaultKey
    const values = 'values' in param && isRecord(param.values) ? param.values : {}
    return { key, values }
  }

  return { key: defaultKey, values: {} }
}

const zodIssueCodeInvalidType = (issue: ZodInvalidTypeIssue, field?: string): string => {
  if (issue.received === ZodParsedType.undefined) {
    return t('zod.errors.invalid_type_received_undefined', { field })
  }

  if (issue.received === ZodParsedType.null) {
    return t('zod.errors.invalid_type_received_null', { field })
  }

  return t('zod.errors.invalid_type', {
    field,
    expected: t(`zod.types.${issue.expected}`, {
      defaultValue: issue.expected,
    }),
    received: t(`zod.types.${issue.received}`, {
      defaultValue: issue.received,
    }),
  })
}

const zodIssueCodeInvalidLiteral = (issue: ZodInvalidLiteralIssue, field?: string): string => {
  return t('zod.errors.invalid_literal', {
    field,
    expected: JSON.stringify(issue.expected),
  })
}

const zodIssueCodeUnrecognizedKeys = (issue: ZodUnrecognizedKeysIssue, field?: string): string => {
  return t('zod.errors.unrecognized_keys', {
    field,
    keys: joinValues(issue.keys, ', '),
    count: issue.keys.length,
  })
}

const zodIssueCodeInvalidUnion = (_issue: ZodInvalidUnionIssue, field?: string): string => {
  return t('zod.errors.invalid_union', { field })
}

const zodIssueCodeInvalidUnionDiscriminator = (issue: ZodInvalidUnionDiscriminatorIssue, field?: string): string => {
  return t('zod.errors.invalid_union_discriminator', {
    field,
    options: joinValues(issue.options),
  })
}

const zodIssueCodeInvalidEnumValues = (issue: ZodInvalidEnumValueIssue, field?: string): string => {
  return t('zod.errors.invalid_enum_value', {
    field,
    options: joinValues(issue.options),
    received: issue.received,
  })
}

const zodIssueCodeInvalidArguments = (_issue: ZodInvalidArgumentsIssue, field?: string): string => {
  return t('zod.errors.invalid_arguments', { field })
}

const zodIssueCodeInvalidReturnType = (_issue: ZodInvalidReturnTypeIssue, field?: string): string => {
  return t('zod.errors.invalid_return_type', { field })
}

const zodIssueCodeInvalidDate = (_issue: ZodInvalidDateIssue, field?: string): string => {
  return t('zod.errors.invalid_date', { field })
}

const zodIssueCodeInvalidString = (issue: ZodInvalidStringIssue, field?: string): string => {
  if (typeof issue.validation === 'object') {
    if ('startsWith' in issue.validation) {
      return t(`zod.errors.invalid_string.startsWith`, {
        field,
        startsWith: issue.validation.startsWith,
      })
    }

    if ('endsWith' in issue.validation) {
      return t(`zod.errors.invalid_string.endsWith`, {
        field,
        endsWith: issue.validation.endsWith,
      })
    }
  }

  return t(`zod.errors.invalid_string.${issue.validation}`, {
    field,
    validation: t(`zod.validations.${issue.validation}`, {
      defaultValue: issue.validation,
    }),
  })
}

const zodIssueCodeTooSmall = (issue: ZodTooSmallIssue, field?: string): string => {
  if (issue.type === 'string' && issue.minimum === 1 && issue.inclusive && !issue.exact) {
    return t('zod.errors.mandatory', { field })
  }

  const minimum = issue.type === 'date' ? new Date(issue.minimum as number) : issue.minimum

  if (issue.exact) {
    return t(`zod.errors.too_small.${issue.type}.exact`, {
      field,
      minimum,
    })
  }

  return t(`zod.errors.too_small.${issue.type}.${issue.inclusive ? 'inclusive' : 'not_inclusive'}`, {
    field,
    minimum,
  })
}

const zodIssueCodeTooBig = (issue: ZodTooBigIssue, field?: string): string => {
  const maximum = issue.type === 'date' ? new Date(issue.maximum as number) : issue.maximum

  if (issue.exact) {
    return t(`zod.errors.too_big.${issue.type}.exact`, {
      field,
      maximum,
    })
  }

  return t(`zod.errors.too_big.${issue.type}.${issue.inclusive ? 'inclusive' : 'not_inclusive'}`, {
    field,
    maximum,
  })
}

const zodIssueCodeCustom = (issue: ZodCustomIssue, field?: string): string => {
  const { key, values } = getKeyAndValues(issue.params?.i18n, 'zod.errors.custom')

  return t(key, { ...values, field })
}

const zodIssueCodeInvalidIntersectionTypes = (_issue: ZodInvalidIntersectionTypesIssue, field?: string): string => {
  return t('zod.errors.invalid_intersection_types', { field })
}

const zodIssueCodeNotMultipleOf = (issue: ZodNotMultipleOfIssue, field?: string): string => {
  return t('zod.errors.not_multiple_of', {
    field,
    multipleOf: issue.multipleOf,
  })
}

const zodIssueCodeNotFinite = (_issue: ZodNotFiniteIssue, field?: string): string => {
  return t('zod.errors.not_finite', { field })
}

export const resolveFieldLabel = (key: string): string => {
  const i18nKey = `fields.${key}`
  return te(i18nKey) ? t(i18nKey) : key
}

export const translateZodError = (issue: ZodIssueOptionalMessage, field?: string): string => {
  const methods: Record<ZodIssueCode, (issue: never, field?: string) => string> = {
    [ZodIssueCode.invalid_type]: zodIssueCodeInvalidType,
    [ZodIssueCode.invalid_literal]: zodIssueCodeInvalidLiteral,
    [ZodIssueCode.unrecognized_keys]: zodIssueCodeUnrecognizedKeys,
    [ZodIssueCode.invalid_union]: zodIssueCodeInvalidUnion,
    [ZodIssueCode.invalid_union_discriminator]: zodIssueCodeInvalidUnionDiscriminator,
    [ZodIssueCode.invalid_enum_value]: zodIssueCodeInvalidEnumValues,
    [ZodIssueCode.invalid_arguments]: zodIssueCodeInvalidArguments,
    [ZodIssueCode.invalid_return_type]: zodIssueCodeInvalidReturnType,
    [ZodIssueCode.invalid_date]: zodIssueCodeInvalidDate,
    [ZodIssueCode.invalid_string]: zodIssueCodeInvalidString,
    [ZodIssueCode.too_small]: zodIssueCodeTooSmall,
    [ZodIssueCode.too_big]: zodIssueCodeTooBig,
    [ZodIssueCode.custom]: zodIssueCodeCustom,
    [ZodIssueCode.invalid_intersection_types]: zodIssueCodeInvalidIntersectionTypes,
    [ZodIssueCode.not_multiple_of]: zodIssueCodeNotMultipleOf,
    [ZodIssueCode.not_finite]: zodIssueCodeNotFinite,
  }

  if (issue.message) {
    return issue.message
  }

  if (methods[issue.code]) {
    return methods[issue.code](issue as never, field)
  }

  return t('zod.errors.custom', { field })
}

z.setErrorMap((issue) => ({
  message: translateZodError(issue),
}))
